import { useEffect, useMemo, useState } from 'react';
import { useSafeRefetch } from '@/shared/hooks';
import {
  useGetBannersQuery,
  useGetCategoriesQuery,
  useGetLocationsQuery,
  useGetMenuItemsQuery,
  useGetReservationByUrlQuery,
} from '@/entities';
import { useGetLocationId } from './useGetLocationId';

export const useGetApiInfo = () => {
  const locationId = useGetLocationId();
  const [isInitialMenuRefetch, setIsInitialMenuRefetch] = useState(true);
  const [isInitialCategoriesRefetch, setIsInitialCategoriesRefetch] = useState(true);

  const {
    data: locationsData,
    error: locationsError,
    refetch: locationsRefetch,
    isLoading: locationsLoading,
    isFetching: locationsFetching,
  } = useGetLocationsQuery();

  const resolvedLocationId = useMemo(
    () => (locationsData?.data?.some((loc) => loc.id === locationId) ? locationId : undefined),
    [locationsData?.data, locationId],
  );

  const isLocationPending = Boolean(locationId) && locationsLoading;

  const {
    data: menuItemsData,
    error: menuItemsError,
    refetch: menuItemsRefetch,
    isFetching: menuItemsFetching,
    isUninitialized: menuItemsUninitialized,
  } = useGetMenuItemsQuery({ locationId: resolvedLocationId }, { skip: isLocationPending });

  const {
    data: categoriesData,
    error: categoriesError,
    refetch: categoriesRefetch,
    isFetching: categoriesFetching,
    isUninitialized: categoriesUninitialized,
  } = useGetCategoriesQuery({ locationId: resolvedLocationId }, { skip: isLocationPending });

  const menuItemsLoading = isLocationPending || (!menuItemsData && !menuItemsError);
  const categoriesLoading = isLocationPending || (!categoriesData && !categoriesError);

  // External consumers (CatalogPage, BasketPage) call these on mount; while the query is skipped it is
  // uninitialized and a raw refetch() would throw, so use safe wrappers that no-op until it has started.
  const safeMenuItemsRefetch = useSafeRefetch(menuItemsRefetch, menuItemsUninitialized);
  const safeCategoriesRefetch = useSafeRefetch(categoriesRefetch, categoriesUninitialized);

  const {
    data: bannersData,
    error: bannersError,
    refetch: bannersRefetch,
    isLoading: bannersLoading,
    isFetching: bannersFetching,
  } = useGetBannersQuery();

  const {
    data: dataReservationByUrl,
    isLoading: isUrlLoading,
    isFetching: isUrlFetching,
  } = useGetReservationByUrlQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const locations = useMemo(() => locationsData?.data ?? [], [locationsData?.data]);
  const menuItems = useMemo(() => menuItemsData?.data ?? [], [menuItemsData?.data]);
  const categories = useMemo(() => categoriesData?.data ?? [], [categoriesData?.data]);
  const banners = useMemo(() => bannersData?.data ?? [], [bannersData?.data]);
  const reservationUrl = useMemo(() => dataReservationByUrl?.data?.url ?? undefined, [dataReservationByUrl?.data]);

  useEffect(() => {
    locationsRefetch();
  }, [locationsRefetch]);

  useEffect(() => {
    if (isLocationPending || menuItemsUninitialized) return;
    menuItemsRefetch();
  }, [menuItemsRefetch, resolvedLocationId, isLocationPending, menuItemsUninitialized]);

  useEffect(() => {
    if (!isLocationPending && !menuItemsFetching) setIsInitialMenuRefetch(false);
  }, [menuItemsFetching, isLocationPending]);

  useEffect(() => {
    if (isLocationPending || categoriesUninitialized) return;
    categoriesRefetch();
  }, [categoriesRefetch, resolvedLocationId, isLocationPending, categoriesUninitialized]);

  useEffect(() => {
    if (!isLocationPending && !categoriesFetching) setIsInitialCategoriesRefetch(false);
  }, [categoriesFetching, isLocationPending]);

  useEffect(() => {
    bannersRefetch();
  }, [bannersRefetch]);

  return {
    banners,
    locations,
    menuItems,
    categories,
    bannersLoading,
    locationsLoading,
    menuItemsLoading,
    locationsRefetch,
    menuItemsRefetch: safeMenuItemsRefetch,
    categoriesRefetch: safeCategoriesRefetch,
    setIsInitialMenuRefetch,
    setIsInitialCategoriesRefetch,
    reservationUrl,
    catalogError: locationsError || categoriesError || menuItemsError || bannersError,
    isLoading: locationsLoading || menuItemsLoading || categoriesLoading || bannersLoading || isUrlLoading,
    isFetching:
      locationsFetching || bannersFetching || isInitialMenuRefetch || isInitialCategoriesRefetch || isUrlFetching,
  };
};
